<script setup lang="ts">
import { computed, ref, useId, watch } from "vue";
import { useI18n } from "vue-i18n";
import { TableFilterCondition } from "../../../types/TableFilter.types";
import VInputNumber from "../../InputNumber/VInputNumber.vue";
import FilterConditionSelect from "./FilterConditionSelect.vue";

interface Props {
  modelValue: [number, number] | number | null
  condition: TableFilterCondition
  placeholder?: string
  /** Id of the element that names the filter; its controls are labelled by it. */
  labelId?: string
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: "",
  labelId: undefined,
});

const emit = defineEmits<{
  "update:modelValue": [value: [number, number] | number | null]
  "update:condition": [condition: TableFilterCondition]
}>();

const { t } = useI18n();
const a11yId = useId();
const minLabelId = `${a11yId}-min`;
const maxLabelId = `${a11yId}-max`;

// The range inputs are named after the filter and their bound, such as "Price Min".
function boundLabelledby(boundLabelId: string): string {
  return props.labelId ? `${props.labelId} ${boundLabelId}` : boundLabelId;
}

const conditionOptions = [
  { value: TableFilterCondition.EQUALS, name: t("filters.conditions.equals") },
  { value: TableFilterCondition.NOT_EQUALS, name: t("filters.conditions.not_equals") },
  {
    value: TableFilterCondition.GREATER_THAN,
    name: t("filters.conditions.greater_than"),
  },
  {
    value: TableFilterCondition.GREATER_THAN_OR_EQUAL,
    name: t("filters.conditions.greater_than_or_equal"),
  },
  { value: TableFilterCondition.LESS_THAN, name: t("filters.conditions.less_than") },
  {
    value: TableFilterCondition.LESS_THAN_OR_EQUAL,
    name: t("filters.conditions.less_than_or_equal"),
  },
  { value: TableFilterCondition.BETWEEN, name: t("filters.conditions.between") },
];

const localCondition = ref(props.condition);
const minValue = ref<number | undefined>(
  Array.isArray(props.modelValue) ? props.modelValue[0] : (props.modelValue as number | undefined),
);
const maxValue = ref<number | undefined>(
  Array.isArray(props.modelValue) ? props.modelValue[1] : undefined,
);

watch(() => props.modelValue, (val) => {
  minValue.value = Array.isArray(val) ? val[0] : (val as number | undefined);
  maxValue.value = Array.isArray(val) ? val[1] : undefined;
});
watch(() => props.condition, (val) => {
  localCondition.value = val;
});

const isBetween = computed(() => localCondition.value === TableFilterCondition.BETWEEN);

function updateValue() {
  if (isBetween.value && minValue.value !== undefined && maxValue.value !== undefined) {
    emit("update:modelValue", [minValue.value, maxValue.value]);
  } else if (!isBetween.value && minValue.value !== undefined) {
    emit("update:modelValue", minValue.value);
  } else {
    emit("update:modelValue", null);
  }
}

function updateCondition(condition: TableFilterCondition) {
  localCondition.value = condition;
  emit("update:condition", condition);
  if (condition !== TableFilterCondition.BETWEEN) {
    maxValue.value = undefined;
  }
  updateValue();
}

function updateMin(value: number | null | undefined) {
  minValue.value = value ?? undefined;
  updateValue();
}

function updateMax(value: number | null | undefined) {
  maxValue.value = value ?? undefined;
  updateValue();
}
</script>

<template>
  <div class="flex flex-col gap-2">
    <FilterConditionSelect
      :model-value="localCondition"
      :options="conditionOptions"
      :label-id="labelId"
      @update:model-value="updateCondition"
    />
    <div v-if="isBetween" class="flex gap-2">
      <span :id="minLabelId" class="sr-only">{{ t("filters.min") }}</span>
      <span :id="maxLabelId" class="sr-only">{{ t("filters.max") }}</span>
      <VInputNumber
        clearable
        :model-value="minValue"
        :aria-labelledby="boundLabelledby(minLabelId)"
        :placeholder="t('filters.min')"
        @update:model-value="updateMin"
      />
      <VInputNumber
        clearable
        :model-value="maxValue"
        :aria-labelledby="boundLabelledby(maxLabelId)"
        :placeholder="t('filters.max')"
        @update:model-value="updateMax"
      />
    </div>
    <VInputNumber
      v-else
      clearable
      :model-value="minValue"
      :aria-labelledby="labelId"
      :placeholder="placeholder"
      @update:model-value="updateMin"
    />
  </div>
</template>
